// ADMIN-only add of a Criteria Checklist catalog item.
// Creates a CriteriaCatalogItem (the template). Lane is fixed by the analyst
// page the Add button lives on; Tier is chosen here. The Code is generated
// server-side (<LaneLetter>-<TierShorthand><ordinal>) — never typed by the
// admin (non-conforming codes would break the cross-references in the seed
// prose). The new active item appears on every deal (including in-progress
// ones) on the next analyst-tab load via the lazy-create path. Mirrors
// <CriteriaCatalogEditModal>.

import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Badge, Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useCreateCatalogItem } from "@/hooks/dealFlow/useDealFlow";
import type { CriteriaLane, CriteriaTier } from "@/api/dealFlow/types";

interface CriteriaCatalogCreateModalProps {
  open: boolean;
  onClose: () => void;
  lane: CriteriaLane;
}

const TITLE_MAX = 255;
const ESCALATION_MAX = 64;

const LANE_LABEL: Record<CriteriaLane, string> = {
  A1: "A1 · Underwriter",
  A2: "A2 · Financial",
  A3: "A3 · Legal",
  A4: "A4 · Business",
};

const LANE_LETTER: Record<CriteriaLane, string> = {
  A1: "S",
  A2: "F",
  A3: "L",
  A4: "M",
};

const TIER_OPTIONS: { value: CriteriaTier; label: string; shorthand: string }[] = [
  { value: "DealKiller", label: "Deal Killer", shorthand: "DK" },
  { value: "RedFlag", label: "Red Flag", shorthand: "RF" },
  { value: "NeedsImprovement", label: "Needs Improvement", shorthand: "NI" },
];

export function CriteriaCatalogCreateModal({
  open,
  onClose,
  lane,
}: CriteriaCatalogCreateModalProps) {
  const toast = useToast();
  const create = useCreateCatalogItem();

  const [tier, setTier] = useState<CriteriaTier>("RedFlag");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [requiredAction, setRequiredAction] = useState("");
  const [escalationPath, setEscalationPath] = useState("");
  const [isCommonExpected, setIsCommonExpected] = useState(false);
  const [resolutionGuidance, setResolutionGuidance] = useState("");

  // Reset to a blank form whenever the modal is (re)opened.
  useEffect(() => {
    if (!open) return;
    setTier("RedFlag");
    setTitle("");
    setDescription("");
    setRequiredAction("");
    setEscalationPath("");
    setIsCommonExpected(false);
    setResolutionGuidance("");
  }, [open]);

  const shorthand =
    TIER_OPTIONS.find((t) => t.value === tier)?.shorthand ?? "";
  const codePreview = `${LANE_LETTER[lane]}-${shorthand}?`;

  const titleValid = title.trim().length > 0 && title.trim().length <= TITLE_MAX;
  const descriptionValid = description.trim().length > 0;
  const requiredActionValid = requiredAction.trim().length > 0;
  const escalationValid = escalationPath.trim().length <= ESCALATION_MAX;
  const formValid =
    titleValid && descriptionValid && requiredActionValid && escalationValid;

  const onSave = async () => {
    try {
      const created = await create.mutateAsync({
        lane,
        tier,
        title: title.trim(),
        description: description.trim(),
        requiredAction: requiredAction.trim(),
        escalationPath: escalationPath.trim() === "" ? null : escalationPath.trim(),
        isCommonExpected,
        resolutionGuidance:
          resolutionGuidance.trim() === "" ? null : resolutionGuidance.trim(),
      });
      toast.success(`${created.code} added. Appears on all deals.`);
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="Add criteria"
      description="Adds a new catalog item that applies to every deal — including in-progress ones."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={!formValid || create.isPending}
          >
            {create.isPending ? "Adding…" : "Add criteria"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div className="flex flex-wrap items-center gap-space-2">
          <Badge status="draft">{codePreview}</Badge>
          <span className="text-caption text-gray-mid">{LANE_LABEL[lane]}</span>
        </div>

        <FormField label="Tier" required>
          <select
            className="px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure"
            value={tier}
            onChange={(e) => setTier(e.target.value as CriteriaTier)}
          >
            {TIER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          label="Code (auto-generated on save)"
          helperText="The lane and tier set the prefix; the next sequential number is assigned when you save."
        >
          <Input value={codePreview} readOnly disabled />
        </FormField>

        <FormField label="Title" required htmlFor="criteria-create-title">
          <Input
            id="criteria-create-title"
            value={title}
            maxLength={TITLE_MAX}
            invalid={title.length > 0 && !titleValid}
            onChange={(e) => setTitle(e.target.value)}
          />
        </FormField>

        <FormField label="Description" required htmlFor="criteria-create-description">
          <Textarea
            id="criteria-create-description"
            rows={6}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>

        <FormField label="Required action" required htmlFor="criteria-create-required-action">
          <Textarea
            id="criteria-create-required-action"
            rows={4}
            value={requiredAction}
            onChange={(e) => setRequiredAction(e.target.value)}
          />
        </FormField>

        <FormField
          label="Escalation path"
          helperText="e.g. E2, E2+E4. Leave blank for none."
        >
          <Input
            value={escalationPath}
            maxLength={ESCALATION_MAX}
            invalid={!escalationValid}
            onChange={(e) => setEscalationPath(e.target.value)}
          />
        </FormField>

        <section className="flex flex-col gap-space-2 border border-border-light rounded-md p-space-3">
          <label className="inline-flex items-center gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={isCommonExpected}
              onChange={(e) => setIsCommonExpected(e.target.checked)}
            />
            Commonly observed (shows the calibration callout on this item)
          </label>
          <FormField label="Callout body shown to analysts when this is commonly observed.">
            <Textarea
              rows={3}
              value={resolutionGuidance}
              disabled={!isCommonExpected}
              placeholder="Calibration guidance — e.g. expected in 30-50% of deals; weigh severity in context."
              onChange={(e) => setResolutionGuidance(e.target.value)}
            />
          </FormField>
        </section>
      </div>
    </Modal>
  );
}
